// ============================================
// FILE: src/components/modals/ProductDetailsModal.jsx
// ============================================
//
// Product detail for non-fashion categories.
//
// Shares the fashion PDP's shell and components — gallery, price block,
// reviews, similar/recently-viewed rails — so a shopper moving between a
// grocery and an apparel product does not land on two different-looking pages.
//
// What stays grocery-specific: weight-aware quantity presets (0.5 / 1 / 2 / 3
// for kg), the "Subscribe & Save" path, and a flat variant picker, because a
// grocery variant is "500ml / 1L" rather than a colour and a size.

import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, ShoppingCart, Plus, Minus, Heart, Truck, RotateCcw, BadgeCheck } from 'lucide-react';
import FashionProductGallery from '../fashion/FashionProductGallery';
import ProductRail from '../fashion/ProductRail';
import ProductReviews from '../fashion/ProductReviews';
import QuickCheckoutButton from '../fashion/QuickCheckoutButton';
import { normalizeMedia } from '../../utils/mediaHelpers';
import { addRecentlyViewed, getRecentlyViewedExcluding } from '../../utils/recentlyViewed';
import {
  getRegularPrice, getFinalPrice, hasDiscount, getDiscountPercent,
  getActiveVariants, getVariantFinalPrice,
} from '../../utils/priceHelpers';
import useIsDesktop from '../../utils/useIsDesktop';
import apiService from '../../apiService';
import QuantityTextBox from '../QuantityTextBox';
import OptionDropdown from '../fashion/OptionDropdown';
import { usesQuantityTextBox, QUANTITY_MAX } from '../../utils/quantityHelpers';

const ASSURANCES = [
  { Icon: RotateCcw, label: 'Easy returns' },
  { Icon: Truck, label: 'Free delivery' },
  { Icon: BadgeCheck, label: 'Quality checked' },
];

const ProductDetailsModal = ({
  isOpen,
  onClose,
  product: initialProduct,
  addToCart,
  cart = [],
  getCartCount,
  getCartTotal,
  openCheckout,
  showToast,
  favorites = [],
  toggleFavorite,
  setShowSubscription,
  appTheme = 'blue',
}) => {
  const isDesktop = useIsDesktop();

  const [product, setProduct] = useState(initialProduct);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [recent, setRecent] = useState([]);
  const [railsLoading, setRailsLoading] = useState(false);

  // Paint from the card payload immediately, then merge the full detail so
  // media, specs and reviews fill in without a blank screen.
  useEffect(() => {
    setProduct(initialProduct);

    let cancelled = false;
    const key = initialProduct?.slug || initialProduct?.id;
    if (!isOpen || key == null) return undefined;

    apiService.getProductDetail(key).then((resp) => {
      const full = resp?.data?.product;
      if (!cancelled && full) setProduct((current) => ({ ...(current || {}), ...full }));
    });

    return () => { cancelled = true; };
  }, [isOpen, initialProduct]);

  const activeVariants = getActiveVariants(product);
  const hasVariantOptions = activeVariants.length > 0;

  // Default to the flagged variant, else the first in stock, else the first.
  useEffect(() => {
    if (!hasVariantOptions) {
      setSelectedVariantId(null);
      return;
    }
    const preferred =
      activeVariants.find((v) => v.is_default) ||
      activeVariants.find((v) => v.is_in_stock) ||
      activeVariants[0];

    setSelectedVariantId(preferred ? preferred.id : null);
    setQuantity(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    const previousBody = document.body.style.overflow;
    const previousHtml = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBody;
      document.documentElement.style.overflow = previousHtml;
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && product?.id != null) {
      apiService.trackPageView(`product:${product.id}`, {
        title: product.name, kind: 'product', product_id: product.id,
      });
    }
  }, [isOpen, product?.id, product?.name]);

  // Similar + recently viewed. Ids are read before recording this product, so
  // the rail shows what came before rather than the page you are on.
  useEffect(() => {
    const currentId = product?.id;
    if (!isOpen || currentId == null) return undefined;

    let cancelled = false;

    (async () => {
      setRailsLoading(true);
      const previousIds = getRecentlyViewedExcluding(currentId, 12);
      addRecentlyViewed(currentId);

      const [similarRes, recentRes] = await Promise.all([
        apiService.getSimilarProducts(currentId, 12),
        previousIds.length > 0
          ? apiService.getProductsByIds(previousIds)
          : Promise.resolve({ data: { products: [] } }),
      ]);

      if (cancelled) return;
      setSimilar(similarRes?.data?.products || []);
      setRecent(recentRes?.data?.products || []);
      setRailsLoading(false);
    })();

    return () => { cancelled = true; };
  }, [isOpen, product?.id]);

  const media = useMemo(() => normalizeMedia(product), [product]);

  const selectedVariant = hasVariantOptions
    ? activeVariants.find((v) => String(v.id) === String(selectedVariantId)) || null
    : null;

  const effectiveFinalPrice = selectedVariant
    ? getVariantFinalPrice(selectedVariant, product)
    : getFinalPrice(product);

  const isVariantOutOfStock = Boolean(selectedVariant) && !selectedVariant.is_in_stock;

  if (!isOpen || !product) return null;

  const favArray = Array.isArray(favorites) ? favorites : [];
  const isFav = favArray.some((item) => String(item?.id) === String(product.id));
  const discounted = hasDiscount(product);

  // ---- Grocery quantity presets ---------------------------------------- //
  const getWeightParts = () => {
    if (product.weight !== undefined && product.weight !== null && product.weight !== '') {
      const match = String(product.weight).trim().match(/^(\d*\.?\d+)\s*([a-zA-Z]*)$/);
      if (match) return { value: parseFloat(match[1]), unit: match[2] || product.unit || '' };
    }
    return { value: null, unit: product.unit || '' };
  };

  const isWeightUnit = (u) => ['kg', 'gram', 'g'].includes(String(u || '').toLowerCase().trim());
  const formatUnit = (u) => {
    const s = String(u || '').trim();
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const { unit } = getWeightParts();
  const quantityOptions = isWeightUnit(unit) ? [0.5, 1, 2, 3] : [1, 2, 3, 4];
  // Apparel / gardening products take a typed quantity instead of quick picks.
  const typedQuantity = usesQuantityTextBox(product);

  const handleAddToCart = () => {
    if (isVariantOutOfStock) {
      showToast?.('This option is out of stock', 'error');
      return;
    }

    // Byte-identical to what cartHelpers expects, so the cart line is still
    // keyed by `${id}::${variant_id}` exactly as before.
    const payload = selectedVariant
      ? {
          ...product,
          variant_id: selectedVariant.id,
          variant_name: selectedVariant.name,
          price: selectedVariant.price != null ? selectedVariant.price : product.price,
          sale_price: selectedVariant.sale_price != null ? selectedVariant.sale_price : null,
          final_price: effectiveFinalPrice,
        }
      : product;

    addToCart(payload, quantity);
    const label = selectedVariant ? `${product.name} (${selectedVariant.name})` : product.name;
    showToast?.(`${quantity}× ${label} added to cart`, 'success');
  };

  const buyBox = (
    <div className={isDesktop ? 'flex-1 min-w-0 pl-2' : 'px-4'}>
      {product.brand && (
        <p style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--fx-ink-3)', fontWeight: 700 }}>
          {product.brand}
        </p>
      )}

      <h1 style={{ fontSize: isDesktop ? 24 : 20, fontWeight: 700, color: 'var(--fx-ink)', lineHeight: 1.25, marginTop: 4 }}>
        {product.name}
      </h1>

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {product.weight && (
          <span className="px-2 py-0.5" style={{ background: '#f5f5f6', borderRadius: 2, fontSize: 12, color: 'var(--fx-ink-2)' }}>
            {product.weight}{product.unit ? ` ${formatUnit(product.unit)}` : ''}
          </span>
        )}
        {product.fat && (
          <span className="px-2 py-0.5" style={{ background: '#e8f7f3', borderRadius: 2, fontSize: 12, color: 'var(--fx-green)' }}>
            {product.fat} fat
          </span>
        )}
        {Number(product.rating) > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5" style={{ border: '1px solid var(--fx-line)', borderRadius: 2, fontSize: 12, fontWeight: 700 }}>
            {Number(product.rating).toFixed(1)}
            <span style={{ color: 'var(--fx-green)' }}>★</span>
          </span>
        )}
      </div>

      <div className="mt-5 pt-5" style={{ borderTop: '1px solid var(--fx-line)' }}>
        <div className="flex items-baseline gap-2.5 flex-wrap">
          <span style={{ fontSize: 24, fontWeight: 700 }}>₹{effectiveFinalPrice}</span>
          {discounted && !selectedVariant && (
            <>
              <span style={{ fontSize: 18, color: 'var(--fx-ink-2)' }}>
                MRP <span style={{ textDecoration: 'line-through' }}>₹{getRegularPrice(product)}</span>
              </span>
              <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--fx-off)' }}>
                ({getDiscountPercent(product)}% OFF)
              </span>
            </>
          )}
        </div>
        <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--fx-green)', marginTop: 4 }}>
          inclusive of all taxes
        </p>
      </div>

      {/* Variants are pack sizes here: one dropdown, each entry showing the
          pack and its price. Out-of-stock packs stay listed but disabled. */}
      {hasVariantOptions && (
        <div className="mt-7 mb-6">
          <label className="fx-label" htmlFor="pdp-pack-select">Choose a pack</label>
          <div className="mt-3">
            <OptionDropdown
              id="pdp-pack-select"
              ariaLabel="Choose a pack"
              value={selectedVariantId}
              placeholder="Choose a pack"
              onChange={setSelectedVariantId}
              options={activeVariants.map((variant) => ({
                value: variant.id,
                label: `${variant.name} — ₹${getVariantFinalPrice(variant, product)}${variant.is_in_stock ? '' : ' (out of stock)'}`,
                disabled: !variant.is_in_stock,
              }))}
            />
          </div>
          {isVariantOutOfStock && (
            <p className="mt-2" style={{ fontSize: 13, color: 'var(--fx-pink)' }}>
              This option is currently out of stock.
            </p>
          )}
        </div>
      )}

      <div className="mb-6">
        <span className="fx-label">Quantity</span>

        {/* Apparel / gardening: no quick picks — the quantity is typed below. */}
        {!typedQuantity && (
        <div className="flex flex-wrap items-center gap-2 mt-3 mb-3">
          {quantityOptions.map((qty) => (
            <button
              key={qty}
              type="button"
              onClick={() => setQuantity(qty)}
              className="px-3 py-1.5 text-xs"
              style={{
                border: `1px solid ${quantity === qty ? 'var(--fx-pink)' : 'var(--fx-line)'}`,
                borderRadius: 4,
                color: quantity === qty ? 'var(--fx-pink)' : 'var(--fx-ink-2)',
                fontWeight: 700,
              }}
            >
              {qty}{isWeightUnit(unit) ? ' kg' : ''}
            </button>
          ))}
        </div>
        )}

        <div className={`flex items-center w-fit ${typedQuantity ? 'mt-3' : ''}`} style={{ border: '1px solid var(--fx-line)', borderRadius: 4 }}>
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            disabled={quantity <= 1}
            className="p-3"
            aria-label="Decrease quantity"
          >
            <Minus size={16} style={{ color: quantity <= 1 ? 'var(--fx-ink-3)' : 'var(--fx-ink-2)' }} />
          </button>
          {typedQuantity ? (
            <QuantityTextBox
              value={quantity}
              onChange={setQuantity}
              size="md"
              label=""
              className="px-2"
              style={{ borderLeft: '1px solid var(--fx-line)', borderRight: '1px solid var(--fx-line)' }}
            />
          ) : (
            <span className="px-5 py-3 font-semibold" style={{ borderLeft: '1px solid var(--fx-line)', borderRight: '1px solid var(--fx-line)' }}>
              {quantity}
            </span>
          )}
          <button
            type="button"
            onClick={() => setQuantity((q) => (typedQuantity ? Math.min(QUANTITY_MAX, q + 1) : q + 1))}
            className="p-3"
            aria-label="Increase quantity"
          >
            <Plus size={16} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>
      </div>

      {isDesktop && (
        <div className="flex gap-4">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isVariantOutOfStock}
            className="fx-btn fx-btn-primary"
            style={{ flex: '1 1 0', maxWidth: 280 }}
          >
            <ShoppingCart size={18} />
            {isVariantOutOfStock ? 'Out of Stock' : `Add to Cart — ₹${(effectiveFinalPrice * quantity).toFixed(2)}`}
          </button>

          {typeof toggleFavorite === 'function' && (
            <button type="button" onClick={() => toggleFavorite(product)} className="fx-btn fx-btn-ghost" style={{ flex: '1 1 0', maxWidth: 200 }}>
              <Heart size={18} fill={isFav ? 'currentColor' : 'none'} className={isFav ? 'text-[#ff3f6c]' : ''} />
              {isFav ? 'Saved' : 'Save'}
            </button>
          )}
        </div>
      )}

      {setShowSubscription && Boolean(Number(product.is_subscribable)) && (
        <button
          type="button"
          onClick={() => { setShowSubscription(product); onClose(); }}
          className="fx-btn fx-btn-ghost w-full mt-3"
          style={{ borderColor: 'var(--fx-green)', color: 'var(--fx-green)' }}
        >
          Subscribe &amp; Save 10%
        </button>
      )}

      <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
        <h3 className="fx-label mb-3">Delivery &amp; Returns</h3>
        <div className="space-y-2.5">
          {ASSURANCES.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-2.5">
              <Icon size={16} style={{ color: 'var(--fx-ink-3)' }} />
              <span style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {(product.description || product.sku || product.hsn_code || product.barcode) && (
        <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--fx-line)' }}>
          <h3 className="fx-label mb-3">Product Details</h3>

          {product.description && (
            <p style={{ fontSize: 14, color: 'var(--fx-ink-2)', lineHeight: 1.6 }}>{product.description}</p>
          )}

          {(product.sku || product.hsn_code || product.barcode) && (
            <ul className="mt-4 space-y-1.5">
              {[['SKU', product.sku], ['HSN Code', product.hsn_code], ['Barcode', product.barcode]]
                .filter(([, v]) => v)
                .map(([label, value]) => (
                  <li key={label} style={{ fontSize: 14, color: 'var(--fx-ink-2)' }}>
                    <span style={{ color: 'var(--fx-ink-3)' }}>{label}: </span>{value}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={`fashion-scope fixed inset-0 z-[300] bg-white page-overlay-scroll ${isDesktop ? 'desktop-scrollbar' : ''}`}>
      <div className={`w-full mx-auto ${isDesktop ? 'max-w-[1200px] px-6 pb-16' : 'pb-24'}`}>
        {!isDesktop && (
          <div
            className="sticky top-0 z-10 bg-white px-3 py-3 flex items-center gap-3"
            style={{ borderBottom: '1px solid var(--fx-line)' }}
          >
            <button type="button" onClick={onClose} className="p-1 -ml-1" aria-label="Go back">
              <ArrowLeft size={22} style={{ color: 'var(--fx-ink)' }} />
            </button>
            <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{product.name}</span>
          </div>
        )}

        {isDesktop && (
          <div className="flex items-center gap-2 py-4" style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
            <button type="button" onClick={onClose} style={{ fontWeight: 700, color: 'var(--fx-ink-2)' }}>
              Back
            </button>
            {product.category && (
              <>
                <span>/</span>
                <span>{typeof product.category === 'object' ? product.category?.name : product.category}</span>
              </>
            )}
          </div>
        )}

        <div className={isDesktop ? 'flex gap-8 items-start' : ''}>
          <div className={isDesktop ? 'w-[600px] flex-shrink-0' : 'mb-5'}>
            <FashionProductGallery
              media={media}
              productName={product.name}
              isDesktop={isDesktop}
            />
          </div>
          {buyBox}
        </div>

        <div className={isDesktop ? '' : 'px-4'}>
          <ProductReviews
            product={product}
            isAuthenticated={apiService.isAuthenticated()}
            showToast={showToast}
            isDesktop={isDesktop}
            onSummaryChange={(s) => setProduct((p) => (
              p ? { ...p, rating: s.average, review_count: s.total } : p
            ))}
          />

          <ProductRail
            title="Similar Products"
            products={similar}
            loading={railsLoading && similar.length === 0}
            onOpen={(next) => setProduct(next)}
            toggleFavorite={toggleFavorite}
            favorites={favorites}
            isDesktop={isDesktop}
          />

          <ProductRail
            title="Recently Viewed"
            products={recent}
            onOpen={(next) => setProduct(next)}
            toggleFavorite={toggleFavorite}
            favorites={favorites}
            isDesktop={isDesktop}
          />
        </div>
      </div>


      <QuickCheckoutButton
        cart={cart}
        getCartCount={getCartCount}
        getCartTotal={getCartTotal}
        openCheckout={openCheckout}
        bottomOffset={isDesktop ? 'bottom-6' : 'bottom-24'}
      />

      {/* Mobile action bar */}
      {!isDesktop && (
        <div
          className="fixed inset-x-0 bottom-0 bg-white flex gap-3 px-3 py-2.5"
          style={{ borderTop: '1px solid var(--fx-line)', boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          {typeof toggleFavorite === 'function' && (
            <button type="button" onClick={() => toggleFavorite(product)} className="fx-btn fx-btn-ghost" style={{ flex: '1 1 0' }}>
              <Heart size={17} fill={isFav ? 'currentColor' : 'none'} className={isFav ? 'text-[#ff3f6c]' : ''} />
              {isFav ? 'Saved' : 'Save'}
            </button>
          )}
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isVariantOutOfStock}
            className="fx-btn fx-btn-primary"
            style={{ flex: '1.3 1 0' }}
          >
            <ShoppingCart size={17} />
            {isVariantOutOfStock ? 'Out of Stock' : `Add — ₹${(effectiveFinalPrice * quantity).toFixed(2)}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsModal;
